import { apiClient } from './api';

export interface DisplayStatus {
  pin: string;
  status: 'waiting' | 'connected';
  transportMode: 'websocket' | 'polling';
  fallbackMode: boolean;
  connectedAt: string | null;
  connectedByUserId: number | null;
  connectedTeachingUnitId: number | null;
  connectedContext: {
    source?: string;
    className?: string;
    theme?: string;
    content?: {
      type?: 'image' | 'text';
      imageUrl?: string;
      text?: string;
      title?: string;
      sourcePageUrl?: string;
    } | null;
    contentItems?: Array<{
      id?: string;
      type?: 'image' | 'text' | 'media';
      assetId?: number | null;
      imageUrl?: string;
      mediaUrl?: string;
      mediaType?: 'image' | 'video' | 'pdf' | 'youtube';
      mimeType?: string;
      text?: string;
      title?: string;
      sourcePageUrl?: string;
      createdAt?: string | null;
      layoutX?: number;
      layoutY?: number;
      layoutWidth?: number;
      layoutZ?: number;
      layoutRotation?: number;
    }>;
    drawing?: {
      strokes?: Array<{
        id?: string;
        tool?: string;
        color?: string;
        width?: number;
        alpha?: number;
        points?: Array<{ x: number; y: number }>;
        createdAt?: string | null;
      }>;
      updatedAt?: string | null;
    } | null;
    contentUpdatedAt?: string | null;
    hasPages?: boolean;
    geodreieck?: {
      visible: boolean;
      x: number;
      y: number;
      rotation: number;
      scale: number;
    } | null;
    zirkel?: {
      visible: boolean;
      x: number;
      y: number;
      radius: number;
      legLength: number;
    } | null;
    calculator?: {
      visible: boolean;
      x: number;
      y: number;
    } | null;
    competencyToasts?: Array<{
      id?: string;
      mode?: 'simple' | 'ranking';
      studentId?: string;
      firstName?: string;
      lastName?: string;
      delta?: number;
      previousScore?: number | null;
      newScore?: number | null;
      top5Before?: Array<{
        studentId?: string;
        firstName?: string;
        lastName?: string;
        score?: number;
      }>;
      top5After?: Array<{
        studentId?: string;
        firstName?: string;
        lastName?: string;
        score?: number;
      }>;
      createdAt?: string | null;
    }>;
    lernbueroDashboard?: {
      enabled: boolean;
      sessionId: number | null;
      sessionLabel?: string;
      updatedAt?: string | null;
      serverNowMs?: number;
      students?: Array<{
        unid: string;
        firstName: string;
        lastName: string;
        className?: string;
        count: number;
      }>;
      top10?: Array<{
        rank: number;
        unid: string;
        firstName: string;
        lastName: string;
        className?: string;
        count: number;
      }>;
      groups?: Array<{
        id: number;
        colorClass?: string;
        deadlineMs: number;
        members?: Array<{
          unid: string;
          firstName: string;
          lastName: string;
          className?: string;
          count?: number;
        }>;
      }>;
      absences?: Array<{
        unid: string;
        firstName: string;
        lastName: string;
        className?: string;
        startedAt?: string;
        startedAtMs?: number;
      }>;
    } | null;
    liveLessonOverlays?: {
      absences: Array<{ studentId: string; firstName: string; lastName: string }>;
      help: Array<{ studentId: string; firstName: string; lastName: string }>;
    };
    boardDisplayState?: {
      enabled: boolean;
      scene: 'focus' | 'task' | 'timer_only' | 'progress' | 'room_energy' | 'blank';
      phaseLabel?: string | null;
      timer?: {
        remainingSec: number;
        running: boolean;
      } | null;
      workMode?: 'individual' | 'pair' | 'group' | 'plenum' | null;
      taskText?: string | null;
      progress?: {
        completedPhaseIds: string[];
        total: number;
      } | null;
      roomEnergy?: {
        band: 'quiet' | 'balanced' | 'lively';
        trend?: 'rising' | 'stable' | 'falling';
      } | null;
      updatedAt?: string | null;
      version?: number;
    } | null;
  } | null;
  displayViewport?: {
    width: number;
    height: number;
    pixelRatio: number;
  } | null;
  activePageIndex?: number;
  totalPages?: number;
}

export interface DisplayRegistrationPayload {
  deviceToken?: string;
  platform?: string;
  userAgent?: string;
  capabilities?: {
    features?: Record<string, boolean>;
    screen?: {
      width?: number | null;
      height?: number | null;
      pixelRatio?: number;
    } | null;
    viewport?: {
      width?: number | null;
      height?: number | null;
      pixelRatio?: number;
    } | null;
  };
}

export interface DisplayRegistrationResponse extends DisplayStatus {
  deviceToken: string;
}

export async function registerDisplay(payload: DisplayRegistrationPayload): Promise<DisplayRegistrationResponse> {
  const response = await apiClient.post('/display/register', payload);
  return response.data?.data;
}

export async function fetchDisplayStatus(pin: string): Promise<DisplayStatus> {
  const response = await apiClient.get(`/display/status/${encodeURIComponent(pin)}`);
  return response.data?.data;
}

export async function fetchMyDisplayConnection(): Promise<DisplayStatus | null> {
  const response = await apiClient.get('/display/my-connection');
  return response.data?.data || null;
}

export async function connectDisplay(payload: {
  pin: string;
  teachingUnitId?: number | null;
  context?: {
    source?: string;
    className?: string;
    theme?: string;
  };
}) {
  const response = await apiClient.post('/display/connect', payload);
  return response.data?.data;
}

export async function disconnectDisplay(pin: string) {
  const response = await apiClient.post('/display/disconnect', { pin });
  return response.data?.data;
}

export async function releaseDisplayConnection(payload: { deviceToken: string; pin?: string }) {
  const response = await apiClient.post('/display/release', payload);
  return response.data?.data;
}

export async function presentDisplayImage(payload: {
  pin: string;
  imageUrl: string;
  title?: string;
  sourcePageUrl?: string;
}) {
  const response = await apiClient.post('/display/present-image', payload);
  return response.data?.data;
}

export async function presentDisplayMedia(payload: {
  pin: string;
  assetId?: number | null;
  mediaUrl: string;
  mediaType?: 'image' | 'video' | 'pdf' | 'youtube';
  mimeType?: string;
  title?: string;
  sourcePageUrl?: string;
}) {
  const response = await apiClient.post('/display/present-media', payload);
  return response.data?.data;
}

export async function presentDisplayText(payload: {
  pin: string;
  text: string;
  title?: string;
}) {
  const response = await apiClient.post('/display/present-text', payload);
  return response.data?.data;
}

export async function dismissDisplayItem(payload: { deviceToken: string; pin: string; itemId: string }) {
  const response = await apiClient.post('/display/dismiss-item', payload);
  return response.data?.data;
}

export async function updateDisplayLayout(payload: {
  deviceToken: string;
  pin: string;
  layouts: Array<{
    itemId: string;
    x: number;
    y: number;
    widthPx: number;
    z: number;
    rotationDeg?: number;
  }>;
}) {
  const response = await apiClient.post('/display/update-layout', payload);
  return response.data?.data;
}

export async function appendDisplayDrawingStrokes(payload: {
  deviceToken: string;
  pin: string;
  strokes: Array<{
    id: string;
    tool: string;
    color: string;
    width: number;
    alpha: number;
    points: Array<{ x: number; y: number }>;
    createdAt: string;
  }>;
}) {
  const response = await apiClient.post('/display/drawing/append', payload);
  return response.data?.data;
}

export async function replaceDisplayDrawingStrokes(payload: {
  deviceToken: string;
  pin: string;
  strokes: Array<{
    id: string;
    tool: string;
    color: string;
    width: number;
    alpha: number;
    points: Array<{ x: number; y: number }>;
    createdAt: string;
  }>;
}) {
  const response = await apiClient.post('/display/drawing/replace', payload);
  return response.data?.data;
}

// ─── Snapshots ────────────────────────────────────────────────

export interface DisplaySnapshotSummary {
  id: number;
  teachingUnitId: number | null;
  name: string;
  theme: string;
  className: string;
  pageCount: number;
  folderName: string;
  tags: string[];
  isFavorite: boolean;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface DisplaySnapshotDetail extends DisplaySnapshotSummary {
  contentItems: Array<Record<string, unknown>>;
  contextJson?: Record<string, unknown> | null;
  drawing: Record<string, unknown> | null;
}

export async function saveDisplaySnapshot(payload: {
  pin: string;
  name?: string;
  teachingUnitId?: number | null;
  folderName?: string;
  tags?: string[];
}): Promise<{ id: number; name: string; itemCount: number; pageCount: number; folderName: string; tags: string[]; isFavorite: boolean; createdAt: string }> {
  const response = await apiClient.post('/display/snapshots', payload);
  return response.data?.data;
}

export async function listDisplaySnapshots(params?: {
  teachingUnitId?: number | null;
  folderName?: string;
  favoritesOnly?: boolean;
  q?: string;
}): Promise<DisplaySnapshotSummary[]> {
  const qs = new URLSearchParams();
  if (params?.teachingUnitId) qs.set('teachingUnitId', String(params.teachingUnitId));
  if (params?.folderName !== undefined) qs.set('folderName', String(params.folderName || ''));
  if (params?.favoritesOnly) qs.set('favoritesOnly', '1');
  if (params?.q) qs.set('q', params.q);
  const query = qs.toString();
  const response = await apiClient.get(`/display/snapshots${query ? `?${query}` : ''}`);
  return response.data?.data || [];
}

export async function getDisplaySnapshot(snapshotId: number): Promise<DisplaySnapshotDetail> {
  const response = await apiClient.get(`/display/snapshots/${snapshotId}`);
  return response.data?.data;
}

export async function restoreDisplaySnapshot(snapshotId: number, pin: string): Promise<{ snapshotId: number; pin: string; itemCount: number }> {
  const response = await apiClient.post(`/display/snapshots/${snapshotId}/restore`, { pin });
  return response.data?.data;
}

export async function deleteDisplaySnapshot(snapshotId: number): Promise<void> {
  await apiClient.delete(`/display/snapshots/${snapshotId}`);
}

export async function updateDisplaySnapshot(snapshotId: number, payload: { name?: string; folderName?: string; tags?: string[]; isFavorite?: boolean }): Promise<void> {
  await apiClient.put(`/display/snapshots/${snapshotId}`, payload);
}

// ─── Teacher-auth actions (no deviceToken required) ───────────────────────────

export async function teacherDismissDisplayItem(payload: { pin: string; itemId: string }): Promise<void> {
  await apiClient.post('/display/teacher/dismiss-item', payload);
}

export async function teacherUpdateDisplayLayout(payload: {
  pin: string;
  layouts: Array<{ itemId: string; x: number; y: number; widthPx: number; z: number; rotationDeg?: number }>;
}): Promise<void> {
  await apiClient.post('/display/teacher/update-layout', payload);
}

export async function teacherUpdateDisplayTextItem(payload: {
  pin: string;
  itemId: string;
  text?: string;
  title?: string;
}): Promise<void> {
  await apiClient.post('/display/teacher/update-text-item', payload);
}

export async function deviceUpdateDisplayTextItem(payload: {
  deviceToken: string;
  pin: string;
  itemId: string;
  text?: string;
  title?: string;
}): Promise<void> {
  await apiClient.post('/display/update-text-item', payload);
}

export async function teacherUpdateDisplayOverlays(payload: {
  pin: string;
  geodreieck?: { visible: boolean; x: number; y: number; rotation: number; scale: number };
  zirkel?: { visible: boolean; x: number; y: number; radius: number; legLength: number };
  calculator?: { visible: boolean; x: number; y: number };
}): Promise<void> {
  await apiClient.post('/display/teacher/update-overlays', payload);
}

export async function teacherUpdateLernbueroDashboard(payload: {
  pin: string;
  dashboard: {
    enabled: boolean;
    sessionId: number | null;
    sessionLabel?: string;
    updatedAt?: string;
    serverNowMs?: number;
    students?: Array<{ unid: string; firstName: string; lastName: string; className?: string; count: number }>;
    top10?: Array<{ rank: number; unid: string; firstName: string; lastName: string; className?: string; count: number }>;
    groups?: Array<{
      id: number;
      colorClass?: string;
      deadlineMs: number;
      members?: Array<{ unid: string; firstName: string; lastName: string; className?: string; count?: number }>;
    }>;
  };
}): Promise<void> {
  await apiClient.post('/display/teacher/update-lernbuero-dashboard', payload);
}

export async function teacherDisableLernbueroDashboard(pin: string): Promise<void> {
  await apiClient.post('/display/teacher/disable-lernbuero-dashboard', { pin });
}

export async function updateDisplayOverlays(payload: {
  deviceToken: string;
  pin: string;
  geodreieck?: { visible: boolean; x: number; y: number; rotation: number; scale: number };
  zirkel?: { visible: boolean; x: number; y: number; radius: number; legLength: number };
  calculator?: { visible: boolean; x: number; y: number };
}): Promise<void> {
  await apiClient.post('/display/update-overlays', payload);
}

export async function teacherReplaceDisplayDrawingStrokes(payload: {
  pin: string;
  strokes: Array<{
    id: string;
    tool: string;
    color: string;
    width: number;
    alpha: number;
    points: Array<{ x: number; y: number }>;
    createdAt: string;
  }>;
}): Promise<void> {
  await apiClient.post('/display/teacher/drawing/replace', payload);
}

export async function teacherSetActivePage(payload: { pin: string; pageIndex: number }): Promise<{ activePageIndex: number; totalPages: number }> {
  const response = await apiClient.post('/display/teacher/set-active-page', payload);
  return response.data?.data;
}

export async function teacherAddPage(payload: { pin: string }): Promise<{ activePageIndex: number; totalPages: number }> {
  const response = await apiClient.post('/display/teacher/add-page', payload);
  return response.data?.data;
}

export async function deviceSetActivePage(payload: { deviceToken: string; pin: string; pageIndex: number }): Promise<{ activePageIndex: number; totalPages: number }> {
  const response = await apiClient.post('/display/set-active-page', payload);
  return response.data?.data;
}

export async function deviceAddPage(payload: { deviceToken: string; pin: string }): Promise<{ activePageIndex: number; totalPages: number }> {
  const response = await apiClient.post('/display/add-page', payload);
  return response.data?.data;
}

export async function fetchYouTubeTitle(url: string): Promise<{ title: string | null; author: string | null }> {
  try {
    const response = await apiClient.get('/display/youtube-title', { params: { url } });
    return response.data?.data ?? { title: null, author: null };
  } catch {
    return { title: null, author: null };
  }
}
